import React, { useState, useEffect, useRef } from 'react';
import './Timeline.css'
import { Container } from 'react-bootstrap';
import { primaryColor } from '../../../Constants/Colors';

// Custom Timeline Components
const VerticalTimeline = ({ children, lineColor = "#E4E7EB" }) => {
  return (
    <div 
      className="vertical-timeline" 
      style={{ '--line-color': lineColor }}
    >
      {children}
    </div>
  );
};

const VerticalTimelineElement = ({ 
  className = '', 
  contentStyle = {}, 
  contentArrowStyle = {}, 
  date, 
  iconStyle = {}, 
  icon, 
  position = 'right',
  image = null,
  imageAlt = '',
  children 
}) => {
  return (
    <div className={`vertical-timeline-element ${className} ${position}`}>
      <div 
        className="vertical-timeline-element-icon"
        style={iconStyle}
      >
        {icon}
      </div>
      <div 
        className="vertical-timeline-element-content"
        style={contentStyle}
      >
        <div 
          className="vertical-timeline-element-content-arrow"
          style={contentArrowStyle}
        />
        <div className="vertical-timeline-element-date">{date}</div>
        {children}
      </div>
      {image && (
        <div className="vertical-timeline-element-image">
          <img src={image} alt={imageAlt} />
        </div>
      )}
    </div>
  );
};

// Enhanced Timeline Icon Components
const TimelineIcons = {
  Foundation: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z" />
    </svg>
  ),
  Client: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-6h3v-2c0-2.21 1.79-4 4-4s4 1.79 4 4v2h3v6H4z"/>
    </svg>
  ),
  Growth: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z" />
    </svg>
  ),
  Award: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5,16L3,5H1L3,16H5M19,16H21L23,5H21L19,16M6,18C6,19.1 6.9,20 8,20S10,19.1 10,18H20C20,19.1 20.9,20 22,20S24,19.1 24,18H14.5L13,16H8L6.5,18H6Z"/>
    </svg>
  ),
  Global: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,19.93C7.05,19.44 4,16.08 4,12C4,11.38 4.08,10.78 4.21,10.21L9,15V16A1,1 0 0,0 10,17H11V19.93M19.9,17.18C19.64,16.83 19.26,16.64 18.84,16.64H18V14A1,1 0 0,0 17,13H13V11H14A1,1 0 0,0 15,10V9H17A2,2 0 0,0 19,7V4.26C19.81,5.53 20.3,7 20.4,8.58L19.9,17.18Z"/>
    </svg>
  ),
  Digital: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4,6H20V16H4M20,18A2,2 0 0,0 22,16V6C22,4.89 21.1,4 20,4H4C2.89,4 2,4.89 2,6V16A2,2 0 0,0 4,18H0V20H24V18H20Z"/>
    </svg>
  ),
  Sustainability: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"/>
    </svg>
  ),
  Leadership: () => (
    <svg className="timeline-icon-svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.83 16.28,18.08 12.8,18.82V21H11.2V18.82C7.72,18.08 5,14.83 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
    </svg>
  )
};

const Timeline = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [visibleElements, setVisibleElements] = useState(new Set());
  const timelineRef = useRef(null);

  // Corporate history data with enhanced content and images
  const timelineData = [
    {
      id: 1,
      timelineDate: "2010-01-15",
      timelineTitle: "Foundation & Vision",
      timelineSubtitle: "The Beginning",
      timelineDescription: "TechCorp Solutions was founded with a revolutionary vision to transform digital landscapes for enterprises worldwide. Starting from a small garage with just 5 passionate engineers, we laid the foundation for what would become a global technology powerhouse.",
      icon: "Foundation",
      color: "#4F46E5",
      position: "right",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&h=300&fit=crop",
      imageAlt: "Team working in garage startup"
    },
    {
      id: 2,
      timelineDate: "2012-06-20",
      timelineTitle: "First Major Breakthrough",
      timelineSubtitle: "Fortune 500 Success",
      timelineDescription: "A pivotal moment arrived when we secured our first Fortune 500 client, delivering a comprehensive ERP solution that revolutionized their operations and increased efficiency by 40%. This milestone established our reputation as industry innovators.",
      icon: "Client",
      color: "#059669",
      position: "left",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop",
      imageAlt: "Business handshake deal"
    },
    {
      id: 3,
      timelineDate: "2014-03-10",
      timelineTitle: "Strategic Expansion",
      timelineSubtitle: "Building Excellence",
      timelineDescription: "Our team grew exponentially from 5 to 50 talented professionals as we moved to our state-of-the-art headquarters. We established specialized departments for R&D, Sales, and Customer Success, creating a foundation for scalable growth.",
      icon: "Growth",
      color: "#DC2626",
      position: "right",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop",
      imageAlt: "Modern office building"
    },
    {
      id: 4,
      timelineDate: "2016-09-05",
      timelineTitle: "Innovation Recognition",
      timelineSubtitle: "Industry Leader",
      timelineDescription: "Our groundbreaking AI-powered analytics platform earned us the prestigious 'Tech Innovation of the Year' award. This recognition positioned us as thought leaders in emerging technologies and artificial intelligence.",
      icon: "Award",
      color: "#F59E0B",
      position: "left",
      image: "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=400&h=300&fit=crop",
      imageAlt: "Award trophy and recognition"
    },
    {
      id: 5,
      timelineDate: "2018-11-12",
      timelineTitle: "Global Presence",
      timelineSubtitle: "International Expansion",
      timelineDescription: "We made our mark on the global stage by opening cutting-edge offices in London and Singapore. Within the first year, we successfully onboarded clients across three continents, establishing our international footprint.",
      icon: "Global",
      color: "#7C3AED",
      position: "right",
      image: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=400&h=300&fit=crop",
      imageAlt: "Global business world map"
    },
    {
      id: 6,
      timelineDate: "2020-04-18",
      timelineTitle: "Digital Transformation Leadership",
      timelineSubtitle: "Pandemic Response",
      timelineDescription: "During the global pandemic, we became the backbone for over 200 companies transitioning to remote work. Our cloud-first solutions proved essential for business continuity, helping organizations adapt to the new digital reality.",
      icon: "Digital",
      color: "#0891B2",
      position: "left",
      image: "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?w=400&h=300&fit=crop",
      imageAlt: "Remote work digital transformation"
    },
    {
      id: 7,
      timelineDate: "2022-07-25",
      timelineTitle: "Sustainable Growth",
      timelineSubtitle: "Green Innovation",
      timelineDescription: "We achieved carbon-neutral operations and launched our comprehensive Green Tech Initiative. Reaching $100M in annual revenue while maintaining our unwavering commitment to environmental responsibility and sustainable practices.",
      icon: "Sustainability",
      color: "#16A34A",
      position: "right",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop",
      imageAlt: "Green technology sustainability"
    },
    {
      id: 8,
      timelineDate: "2024-12-01",
      timelineTitle: "Market Leadership",
      timelineSubtitle: "Industry Dominance",
      timelineDescription: "Recognized as the #1 digital transformation partner by leading industry analysts. Today, we proudly serve over 1,000 clients globally with our team of 500+ exceptional professionals across 5 countries, continuing to shape the future of technology.",
      icon: "Leadership",
      color: "#BE185D",
      position: "left",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop",
      imageAlt: "Leadership team collaboration"
    }
  ];

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    if (isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute('data-id');
            if (elementId) {
              setVisibleElements(prev => new Set([...prev, elementId]));
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const timelineElements = document.querySelectorAll('.vertical-timeline-element');
    timelineElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [isLoading]);

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Get icon component
  const getIconComponent = (iconName) => {
    const IconComponent = TimelineIcons[iconName] || TimelineIcons.Foundation;
    return <IconComponent />;
  };

  if (isLoading) {
    return (
      <div className="timeline-container">
        <div className="timeline-loading">
          <div className="timeline-spinner"></div>
        </div>
      </div>
    );
  }

  return (
<Container>
 {/* Header Section */}
      <div>
        <h1 style={{color:primaryColor}}>Our History</h1>
        <p>From humble beginnings to digital pioneers – here’s how Ideal evolved over the years.</p>
      </div>
    <div className="timeline-container">
  
      {/* Timeline Section */}
      <div ref={timelineRef} style={{ position: 'relative', zIndex: 1, margin: '0 auto', }}>
        <VerticalTimeline lineColor="#E4E7EB">
          {timelineData.map((timeline) => (
            <VerticalTimelineElement
              key={timeline.id}
              data-id={timeline.id}
              className={`vertical-timeline-element--work ${visibleElements.has(timeline.id.toString()) ? 'animate-in' : ''}`}
              position={timeline.position}
              contentStyle={{
                background: "rgba(255, 255, 255, 0.95)",
                color: "#000000",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "16px"
              }}
              contentArrowStyle={{
                borderRight: timeline.position === 'right' ? "7px solid rgba(255, 255, 255, 0.95)" : "none",
                borderLeft: timeline.position === 'left' ? "7px solid rgba(255, 255, 255, 0.95)" : "none",
              }}
              date={formatDate(timeline.timelineDate)}
              iconStyle={{ 
                background: `linear-gradient(135deg, ${timeline.color} 0%, var(--secondary-color) 100%)`,
                color: "#fff",
                boxShadow: `0 0 0 6px rgba(255, 255, 255, 1), 0 0 0 8px ${timeline.color}, 0 10px 15px -3px rgba(0, 0, 0, 0.1)`
              }}
              icon={getIconComponent(timeline.icon)}
              image={timeline.image}
              imageAlt={timeline.imageAlt}
            >
              <div className="vertical-timeline-element-subtitle">
                {timeline.timelineSubtitle}
              </div>
              <h3 className="vertical-timeline-element-title">
                {timeline.timelineTitle}
              </h3>
              <p className="vertical-timeline-element-details">
                {timeline.timelineDescription}
              </p>
            </VerticalTimelineElement>
          ))}
        </VerticalTimeline>
      </div>
    </div>

</Container>
 
  );
};

export default Timeline;